import * as React from 'react';
import {createNewScheduleCalc} from '@helpers/calendar/CreateNewCalendar';
import {schoolAgeFromFamily} from '@helpers/schoolHelper';
import * as ScheduleActions from '@services/schedule/schedule.action';
import {IFamilyMember} from '@app-types/websocket/family.types';
import StepAdjustDays from './steps/stepAdjustDays.component';
import StepChildren from './steps/stepChildren.component';
import StepStartDate from './steps/StepStartDate';
import StepTemplate from './steps/stepTemplate.component';

import SnackBar from '../common/snackbar';
import {FamilyMemberType} from '@services/family/family.constants';
import TranslateNew from '@translate/TranslateNew';
import {displayErrorMessage} from '@services/user/user.actions';
import {connect} from 'react-redux';
import {ISchedule} from '@app-types/websocket/schedule.types';
import StepTemplateCustom from '@components/guide/steps/stepTemplateCustom.component';
import {IconButton, Typography} from "@material-ui/core";
import StepHandover from "@components/guide/steps/stepHandover.component";
import ProgressBar from "@components/common/progressBar/progressBar";
import {WithStyles, withStyles} from "@material-ui/core/styles";
import {GuideStyles} from "@components/guide/guide.styles";
import Button from "@components/common/button/Button";
import { DialogTop } from '@components/economy/DialogComponens';
import CloseIcon from "@material-ui/icons/Close";
import { holidays as allHolidays } from '@components/calendar2/holidays/holidays';
import { IHoliday } from '@constants/holidays/holidayTypes';
import { getWeekStart } from '@helpers/DateHelper';

const tn: TranslateNew = TranslateNew.getInstance();

interface IGuideProps extends WithStyles<typeof GuideStyles> {
    firstSchedule: boolean;
    family: IFamilyMember[];
    parentA?: number;
    parentB?: number;
    completedCallback?: () => void;
    cancelCallback?: () => void | undefined;
    abortCallback?: (event:any) => void | undefined;
    displayError: (msg: string) => void;
    createSchedule: (data: ISchedule) => void;
    progress?: number | undefined;
    isOnboarding?: boolean;
    nextStep?: (step: number) => void;
    style?:React.CSSProperties;
    holidays:{[date: string]: IHoliday} 
    localization: 'en'|'se';
}

interface IGuideState {
    snackBarOpen: boolean;
    stepErrorType: string;
    template: boolean[];
    schedule: boolean[];
    startDate: Date;
    endDate:Date;
    collectedChildren: number[];
    currentStep: number;
    previousStep: number[];
}

class Guide extends React.Component<IGuideProps, IGuideState> {
    public static defaultProps = {
        isOnboarding: false,
        progress: undefined,
    };

    constructor(props: IGuideProps) {
        super(props);

        this.state = {
            snackBarOpen: false,
            stepErrorType: '',
            currentStep: 0,
            previousStep: [],
            collectedChildren: [],
            startDate: new Date(),
            endDate: new Date(((new Date()).getFullYear()+2)+"-12-31"),
            template: [],
            schedule: [],
        };
    }

    public componentDidMount() {
        history.pushState(null, document.title, window.location.href);
        window.addEventListener('popstate', this.backPressed, true);
    }

    public backPressed = () => {
        if (this.state.currentStep > 0) {
            history.pushState(null, document.title, window.location.href);
            this.setState((prevState) => ({
                currentStep: prevState.previousStep.length > 0
                    ? prevState.previousStep[prevState.previousStep.length - 1]
                    : prevState.currentStep - 1,
                previousStep: prevState.previousStep.slice(0, prevState.previousStep.length - 1),
            }));
        } else {
            history.back();
        }
    }

    public componentWillUnmount() {
        window.removeEventListener('popstate', this.backPressed, true);
    }

    public toggleSnackBar = (state: boolean) => this.setState({snackBarOpen: state});

    public setStepErrorType = (type: string) => this.setState({stepErrorType: type});

    public setStep = (newStep: number) => {
        if (this.state.currentStep !== newStep) {
            if (this.props.firstSchedule && newStep === 1) {
                this.setState((prevState) => ({
                    currentStep: prevState.currentStep === 0 ? 2 : 0,
                    previousStep: [...prevState.previousStep, prevState.currentStep],
                }));
            } else {
                this.setState((prevState) => ({
                    currentStep: newStep,
                    previousStep: [...prevState.previousStep, prevState.currentStep],
                }));
            }
        }
        const elmnt = document.getElementById("page-top");
        if (elmnt !== null) {
            elmnt.scrollIntoView({behavior: 'auto', block: 'start'});
        }

    }

    public setChildren = (collectedChildren: number[]) => {this.setState({collectedChildren});};

    public setDate = (startDate: Date, endDate: Date) => this.setState({startDate, endDate});

    public setTemplate = (template: boolean[]) => this.setState({template, schedule: template});

    public setDays = (schedule: boolean[]) => {
        if (!schedule || schedule.length < 7) return;
        this.setState({
            schedule
        });
    }

    public setHandoverDays = (days: any[]) => {
        const {parentA, parentB} = this.props;
        const {collectedChildren, startDate, endDate} = this.state;

        if (!parentA || !parentB || parentA === parentB) {
            this.props.displayError(tn.lang.newCalendarTwoParents);
            return;
        }

        if (days && days.length) {
            this.createNewScheduleComplete(parentA, parentB, days, collectedChildren, startDate, endDate);
        }
    }

    public render() {

        const {
            snackBarOpen,
            stepErrorType,
            currentStep
        } = this.state;

        return (
            <>
                {(this.props.isOnboarding === false) && (
                    <div style={{right:8, top: 8, position:'fixed', zIndex:2}}>
                    <IconButton onClick={(event) => {if (this.props.abortCallback) {this.props.abortCallback(event);}}}>
                        <CloseIcon />
                    </IconButton>
                    </div>
                )}
                
                {this.renderCurrentStep(currentStep)}
                
                {/* SnackBar to display errors */}
                <SnackBar
                    snackBarOpen={snackBarOpen}
                    stepErrorType={stepErrorType}
                    toggleSnackBar={this.toggleSnackBar}
                />
            </>
        );
    }

    private renderCurrentStep = (step: number) => {
        const {classes: style, progress, family, parentA, parentB, nextStep} = this.props;
        const {
            template,
            startDate,
            schedule
        } = this.state;

        const firstParent: IFamilyMember | undefined = (family && family.length)
            ? family.find((member: IFamilyMember) => member.id === parentA)
            : undefined;

        const secondParent: IFamilyMember | undefined = (family && family.length)
            ? family.find((member: IFamilyMember) => (member.id === parentB))
            : undefined;

        const children = (family && family.length)
            ? family.filter((member: IFamilyMember) => member.role === FamilyMemberType.CHILD)
            : [];

        if (firstParent === undefined || secondParent === undefined || children.length === 0) {
            return (
                <div className={style.container} style={this.props.style}>
                    {this.props.progress && (
                        <div className={style.topBar}>
                            <ProgressBar value={this.props.progress}/>
                        </div>
                    )}
                    <div className={style.middleContent}>
                        <Typography variant="subtitle1">{tn.lang.newCalendarTwoParents}</Typography>
                    </div>
                    {nextStep && (
                        <div className={style.bottomContent}>
                            <Button
                                variant="contained"
                                color="primary"
                                size="large"
                                onClick={() => nextStep(0)}
                            >
                                {tn.lang.back}
                            </Button>
                        </div>
                    )}

                </div>
            );
        }

        switch (step) {
            case 0:
                return (
                    <StepChildren
                        key="1"
                        firstSchedule={this.props.firstSchedule}
                        step={step}
                        progress={progress}
                        setStep={this.setStep}
                        family={this.props.family}
                        setFChildren={this.setChildren}
                        toggleSnackBar={this.toggleSnackBar}
                        setStepErrorType={this.setStepErrorType}
                        cancelCallback={this.props.cancelCallback}
                        style={this.props.style}
                    />
                );
            case 1:
                return (
                    <StepStartDate
                        key="2"
                        step={step}
                        progress={progress}
                        setStep={this.setStep}
                        setDate={this.setDate}
                        style={this.props.style}
                        isOnboarding={this.props.isOnboarding === true}
                    />
                );
            case 2:
                return (
                    <StepTemplate
                        key="3"
                        step={step}
                        progress={progress}
                        firstParent={firstParent}
                        secondParent={secondParent}
                        setStep={this.setStep}
                        setTemplate={this.setTemplate}
                        toggleSnackBar={this.toggleSnackBar}
                        setStepErrorType={this.setStepErrorType}
                        goBack={this.backPressed}
                        style={this.props.style}
                        localization={this.props.localization}
                    />
                );
            case 3:
                return (
                    <StepTemplateCustom
                        key="4"
                        step={step}
                        progress={progress}
                        setStep={this.setStep}
                        setTemplate={this.setTemplate}
                        toggleSnackBar={this.toggleSnackBar}
                        setStepErrorType={this.setStepErrorType}
                        firstParent={firstParent}
                        secondParent={secondParent}
                        goBack={this.backPressed}
                        style={this.props.style}
                        localization={this.props.localization}
                    />
                );
            case 4:
                return (
                    <StepAdjustDays
                        key="5"
                        step={step}
                        progress={progress}
                        setStep={this.setStep}
                        template={template}
                        startDate={startDate}
                        firstParent={firstParent}
                        secondParent={secondParent}
                        setDays={this.setDays}
                        children={children}
                        holidays={this.props.holidays}
                        setHandoverDays={this.setHandoverDays}
                        toggleSnackBar={this.toggleSnackBar}
                        setStepErrorType={this.setStepErrorType}
                        goBack={this.backPressed}
                        inOnboarding={this.props.isOnboarding?true:false}
                        style={this.props.style}
                        localization={this.props.localization}
                    />
                );
            case 5:
                return (
                    <StepHandover
                        key="6"
                        step={step}
                        progress={progress}
                        setStep={this.setStep}
                        days={schedule}
                        firstParent={firstParent}
                        secondParent={secondParent}
                        schoolAge={schoolAgeFromFamily(family)}
                        startDate={startDate}
                        toggleSnackBar={this.toggleSnackBar}
                        setStepErrorType={this.setStepErrorType}
                        setHandoverDays={this.setHandoverDays}
                        goBack={this.backPressed}
                        style={this.props.style}
                    />
                );
            default:
                return (
                    <div className={style.container} style={this.props.style}>
                        {this.props.progress && (
                            <div className={style.topBar}>
                                <ProgressBar value={this.props.progress}/>
                            </div>
                        )}
                        <div className={style.middleContent}>
                            <Typography variant="subtitle1">NON-existant step!</Typography>
                        </div>
                    </div>
                );
        }
    }

    private createNewScheduleComplete = (
        parentA: number,
        parentB: number,
        days: any[],
        children: number[],
        startDate: Date,
        endDate: Date,
    ) => {

        const newSchedule = createNewScheduleCalc(
            days,
            children,
            startDate,
            endDate,
            parentA,
            parentB
        );

        if (this.props.isOnboarding) {
            this.props.createSchedule(newSchedule);
            if (this.props.completedCallback !== undefined) {
                this.props.completedCallback();
            }
        } else {
            this.props.createSchedule(newSchedule);
        }
        if (this.props.completedCallback) {
            this.props.completedCallback();
        }
    }
}



const mapStateToProps = (state: any) => {

    const userId = state.user.session.data.id;
    const family: IFamilyMember[] = state.family.family;

    let parentA: number | undefined;
    let parentB: number | undefined;

    for (const mem of family) {
        if (mem.role === FamilyMemberType.PARENT) {
            if (parentA === undefined) {
                parentA = mem.id;
            } else if (parentB === undefined) {
                parentB = mem.id;
            }
        }
    }

    if (parentB === userId) {
        parentB = parentA;
        parentA = userId;
    }
    const country = (state.user.calendarSettings && state.user.calendarSettings.holidaysCountry)
        ?state.user.calendarSettings.holidaysCountry
        :undefined;

    const holidays: {[date: string]: IHoliday} = allHolidays.getHolidays(country);

    const localization:"en" | "se" = country
    ?(country.toLowerCase() === "us"||country.toLowerCase() === "ca")
      ?"en"
      :"se"
    :getWeekStart();

    return {
        family,
        userId,
        parentA,
        parentB,
        holidays,
        localization,
    };
};

const mapDispatchToProps = {
    displayError: displayErrorMessage,
    createSchedule: ScheduleActions.createSchedule,
};

export default connect(mapStateToProps, mapDispatchToProps)(withStyles(GuideStyles)(Guide));



// WEBPACK FOOTER //
// ./src/components/guide/guide.component.tsx